import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, contentList } from '@/mocks/content';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'update'>('rating');

  const category = categories.find((c) => c.slug === slug);

  const items = useMemo(() => {
    if (!category) return [];
    let filtered = contentList.filter((item) => item.category === category.slug);
    if (sortBy === 'rating') {
      filtered = [...filtered].sort((a, b) => b.rating - a.rating);
    } else {
      filtered = [...filtered].sort(
        (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
      );
    }
    return filtered;
  }, [category, sortBy]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">分类不存在</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <nav className="bg-background-50 border-b border-background-200/70">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-3">
            <ol className="flex items-center gap-2 text-xs text-foreground-500">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              </li>
              <li>
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-[10px]"></i>
                </div>
              </li>
              <li className="text-foreground-700 font-medium">{category.name}</li>
            </ol>
          </div>
        </nav>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className={`${category.icon} text-lg`}></i>
                  </div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    中文字幕{category.name}
                  </h1>
                </div>
                <p className="text-sm text-foreground-600">{category.description}</p>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                <div className="flex bg-background-100 rounded-full p-1">
                  <button
                    onClick={() => setSortBy('rating')}
                    className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                      sortBy === 'rating'
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    评分最高
                  </button>
                  <button
                    onClick={() => setSortBy('update')}
                    className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                      sortBy === 'update'
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    最新更新
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {items.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.id}`}
                  className="group bg-background-100 rounded-xl overflow-hidden transition-all duration-300 hover:bg-background-200/30 cursor-pointer"
                  data-product-shop
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={item.image}
                      alt={`${item.title} 中文字幕${category.name}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3">
                      <span className="px-2 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-bold">
                        {item.rating}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-semibold text-foreground-950 text-sm group-hover:text-primary-500 transition-colors mb-1.5">
                      {item.title}
                    </h3>
                    <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed mb-3">
                      {item.description}
                    </p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {item.genre.slice(0, 2).map((g) => (
                        <span key={g} className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-[10px]">
                          {g}
                        </span>
                      ))}
                      <span className="text-[10px] text-foreground-500 ml-auto whitespace-nowrap">
                        {item.subtitleStatus}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {items.length === 0 && (
              <div className="text-center py-20">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">暂无内容</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}